import type { APIBookingDraft, APIBookingDraftService } from '@/scopes/checkout/infrastructure/api/types';
import type { CheckoutPerBookingService, ServiceMapper, ServiceType } from '../../../domain/types';
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper';
import { createServiceBannersMapper, type ServiceBannersMapper } from '../model-mappers/serviceBannersMapper';
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '../model-mappers/serviceParticipantsMapper';
import { isNil, values } from 'ramda';
import type { AncillariesAddedAndRemovedEventProps } from '@/scopes/checkout/domain/analytics/types';

interface Dependencies {
  serviceName: string;
  serviceTypeMapper: string;
  serviceParticipantsMapper?: ServiceParticipantsMapper;
  serviceOptionMapper?: ServiceOptionMapper;
  serviceBannersMapper?: ServiceBannersMapper;
  serviceType: ServiceType;
}

export const createPerBookingServiceMapper: (dependencies: Dependencies) => ServiceMapper<CheckoutPerBookingService> = (
  dependencies
) => {
  const {
    serviceName,
    serviceType,
    serviceTypeMapper,
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceOptionMapper = createServiceOptionMapper(),
    serviceBannersMapper = createServiceBannersMapper(),
  } = dependencies;

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName);

    if (!service) return null;

    const { aName, options } = service;
    const selectedValue = mapSelectedValue(service);

    return {
      id: aName,
      isSelected: selectedValue ? Boolean(selectedValue) : !isNil(selectedValue),
      type: serviceType,
      selectedValue,
      participants: serviceParticipantsMapper.map(service, bookingDraft),
      options: options.map((option) => serviceOptionMapper.map(option, service, bookingDraft)),
      banners: serviceBannersMapper.map(service),
      isPricePerPerson: service.isPricePerPerson,
    };
  }

  function getServiceName() {
    return serviceName;
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper;
  }

  function mapSelectedValue({ options }: APIBookingDraftService) {
    return options.find(({ isSelected }) => isSelected)?.code ?? null;
  }

  function mapSelection(service: CheckoutPerBookingService) {
    const { participants } = service;

    return Object.values(participants ?? {}).map(({ id, selectedValue }) => ({
      participantId: id,
      value: selectedValue,
    }));
  }

  function mapAncillaryAddedAndRemovedToGA4Result(
    service: CheckoutPerBookingService,
    previousService: CheckoutPerBookingService
  ): {
    ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[];
    ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[];
  } {
    const ancillaryAddMapped = [];
    const ancillaryRemoveMapped = [];

    const previousOptionClicked = previousService?.options.find(({ value }) => value === previousService.selectedValue);
    const newOptionClicked = service?.options.find(({ value }) => value === service.selectedValue);

    if (newOptionClicked) {
      ancillaryAddMapped.push({
        id: newOptionClicked.value || '',
        name: newOptionClicked.label || '',
        price: newOptionClicked?.rawPrice || 0,
        category: `Ancillary > ${newOptionClicked?.badge ? 'Persuasion' : 'Additional cost'} > ${service.type}`,
        quantity: service.isPricePerPerson ? values(service.participants).length : 1,
        serviceType: service.type,
        extraProperties: {},
      });
    }

    if (previousOptionClicked) {
      ancillaryRemoveMapped.push({
        id: previousOptionClicked.value || '',
        name: previousOptionClicked.label || '',
        price: previousOptionClicked?.rawPrice || 0,
        category: `Ancillary > ${previousOptionClicked?.badge ? 'Persuasion' : 'Additional cost'} > ${
          previousService.type
        }`,
        quantity: previousService.isPricePerPerson ? values(previousService.participants).length : 1,
        serviceType: previousService.type,
        extraProperties: {},
      });
    }

    return {
      ancillaryAddMapped,
      ancillaryRemoveMapped,
    };
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapAncillaryAddedAndRemovedToGA4Result,
  };
};
